/**
 * Phase R — Per-section clone of `pages/WMS/LocationSetup.jsx` (FULL Salem parity).
 *
 * MACHINE-GENERATED by `scripts/build_per_section_wms_setup_frontend.py`.
 * Mounted at `/sections/dispatch/:section/wms-setup/<flow>`. All `${apiBase}/...`
 * API calls are rewritten to `/per-section/${slug}/wms-setup/...`.
 *
 * DO NOT hand-edit. Edit Salem's source of truth and re-run the transformer.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, ChevronDown, ChevronRight, Warehouse, Rows3, LayoutGrid, Layers, BoxSelect, Upload, Download, Edit2, Check, X, Tag, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const SLUG_TO_DISPLAY = {
  color_combo: 'Color Combo',
  art_silk: 'Art Silk',
  kids: 'Kids',
};

// PHASE_R_FULL_CLONE = true (sentinel for tests)

const PerSectionLocationSetup = () => {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  const sectionName = SLUG_TO_DISPLAY[slug];
  const basePath = `/sections/dispatch/${slug}`;
  const apiBase = `/per-section/${slug}/wms-setup`;
  const [storageType, setStorageType] = useState('SFG');
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedHalls, setExpandedHalls] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedRacks, setExpandedRacks] = useState({});
  const [expandedLevels, setExpandedLevels] = useState({});
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'hall', 'row', 'rack', 'level', 'bin'
  const [dialogParentId, setDialogParentId] = useState('');
  const [newItemName, setNewItemName] = useState('');
  const [newItemCapacity, setNewItemCapacity] = useState('');
  
  // Import states
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const fileInputRef = useRef(null);
  
  // Edit capacity state
  const [editingLevelId, setEditingLevelId] = useState(null);
  const [editCapacityValue, setEditCapacityValue] = useState('');
  
  // Category configuration for halls
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [selectedHall, setSelectedHall] = useState(null);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);

  useEffect(() => {
    fetchHierarchy();
  }, [storageType]);

  const fetchHierarchy = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`${apiBase}/location-hierarchy?storage_type=${storageType}`);
      setHierarchy(response.data.hierarchy || []);
    } catch (error) {
      toast.error('Failed to fetch location hierarchy');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (type, parentId = '') => {
    setDialogType(type);
    setDialogParentId(parentId);
    setNewItemName('');
    setNewItemCapacity('');
    setDialogOpen(true);
  };
  
  // Import functionality
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };
  
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImporting(true);
    setImportProgress({ phase: 'starting', processed: 0, total: 0 });
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || ''}/api/per-section/${slug}/wms-setup/locations/import?storage_type=${storageType}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('session_token')}`
          },
          body: formData
        }
      );

      // 2026-05-21 — Production was throwing
      //   "ReadableStreamDefaultReader constructor can only accept readable
      //    streams that are not yet locked to a reader"
      // when the body was already consumed by a service worker / CDN
      // interceptor on mwwms.com. Defensive fallbacks:
      //   1. Bail early on a non-OK response with a clear message.
      //   2. Use `response.clone()` when the original body is locked, then
      //      fall through to a one-shot `text()` parse if streaming still
      //      fails (no per-chunk progress, but the import still completes).
      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        toast.error(`Import failed (${response.status}): ${errText || response.statusText}`);
        return;
      }

      let reader;
      try {
        const streamSource = (response.body && !response.body.locked) ? response : response.clone();
        reader = streamSource.body.getReader();
      } catch {
        // Stream not readable — fall back to full text parse below.
        reader = null;
      }
      const decoder = new TextDecoder();

      const parseSseChunk = (text) => {
        const lines = text.split('\n').filter(line => line.startsWith('data:'));
        for (const line of lines) {
          try {
            const data = JSON.parse(line.replace('data: ', ''));
            setImportProgress(data);
            if (data.error) {
              toast.error(data.error);
              setImporting(false);
              return;
            }
            if (data.phase === 'completed') {
              toast.success(`Import complete! Created: ${data.created}, Updated: ${data.updated}`);
              if (data.total_errors > 0) toast.warning(`${data.total_errors} errors occurred`);
              fetchHierarchy();
            }
          } catch (e) {
            // Ignore parsing errors for incomplete chunks
          }
        }
      };

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          parseSseChunk(decoder.decode(value));
        }
      } else {
        const fullText = await response.clone().text();
        parseSseChunk(fullText);
      }
    } catch (error) {
      toast.error('Import failed: ' + error.message);
    } finally {
      setImporting(false);
      setImportProgress(null);
      event.target.value = '';
    }
  };
  
  const handleExportTemplate = async () => {
    try {
      const response = await apiClient.get(`${apiBase}/locations/export-template?storage_type=${storageType}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `wms_locations_${storageType}_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };
  
  // Edit capacity functions
  const startEditCapacity = (levelId, currentCapacity) => {
    setEditingLevelId(levelId);
    setEditCapacityValue(currentCapacity?.toString() || '0');
  };
  
  const cancelEditCapacity = () => {
    setEditingLevelId(null);
    setEditCapacityValue('');
  };
  
  const saveCapacity = async (levelId) => {
    try {
      const capacity = parseInt(editCapacityValue) || 0;
      await apiClient.patch(`${apiBase}/levels/${levelId}/capacity?capacity=${capacity}`);
      toast.success('Capacity updated');
      setEditingLevelId(null);
      fetchHierarchy();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update capacity');
    }
  };

  // Category Management Functions
  const fetchAvailableCategories = async () => {
    try {
      const response = await apiClient.get(`${apiBase}/category4-values`);
      setAvailableCategories(response.data.categories || []);
    } catch (error) {
      toast.error('Failed to fetch categories');
    }
  };

  const openCategoryDialog = (hall) => {
    setSelectedHall(hall);
    setSelectedCategories(hall.categories || []);
    fetchAvailableCategories();
    setCategoryDialogOpen(true);
  };

  const handleCategoryToggle = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const saveHallCategories = async () => {
    if (!selectedHall) return;
    
    try {
      await apiClient.patch(`${apiBase}/locations/${selectedHall.location_id}/categories`, {
        categories: selectedCategories
      });
      toast.success('Hall categories updated');
      setCategoryDialogOpen(false);
      fetchHierarchy();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update categories');
    }
  };

  // SKU Assignment Functions
  const handleCreate = async () => {
    if (!newItemName.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      let endpoint = '';
      let payload = {};

      switch (dialogType) {
        case 'hall':
          endpoint = `${apiBase}/locations`;
          payload = { location_name: newItemName, storage_type: storageType };
          break;
        case 'row':
          endpoint = `${apiBase}/rows`;
          payload = { location_id: dialogParentId, row_name: newItemName };
          break;
        case 'rack':
          endpoint = `${apiBase}/racks`;
          payload = { row_id: dialogParentId, rack_name: newItemName };
          break;
        case 'level':
          endpoint = `${apiBase}/levels`;
          payload = { 
            rack_id: dialogParentId, 
            level_name: newItemName,
            capacity: parseInt(newItemCapacity) || 0
          };
          break;
        case 'bin':
          endpoint = `${apiBase}/bins`;
          payload = { 
            level_id: dialogParentId, 
            bin_name: newItemName,
            capacity: parseInt(newItemCapacity) || 100
          };
          break;
        default:
          return;
      }

      await apiClient.post(endpoint, payload);
      toast.success(`${dialogType.charAt(0).toUpperCase() + dialogType.slice(1)} created successfully`);
      setDialogOpen(false);
      fetchHierarchy();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to create ${dialogType}`);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}? This will also delete all children.`)) {
      return;
    }

    try {
      // Map 'hall' to 'location' for API
      const apiType = type === 'hall' ? 'location' : type;
      await apiClient.delete(`${apiBase}/${apiType}s/${id}`);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted successfully`);
      fetchHierarchy();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to delete ${type}`);
    }
  };

  const toggleHall = (hallId) => {
    setExpandedHalls(prev => ({ ...prev, [hallId]: !prev[hallId] }));
  };

  const toggleRow = (rowId) => {
    setExpandedRows(prev => ({ ...prev, [rowId]: !prev[rowId] }));
  };

  const toggleRack = (rackId) => {
    setExpandedRacks(prev => ({ ...prev, [rackId]: !prev[rackId] }));
  };

  const toggleLevel = (levelId) => {
    setExpandedLevels(prev => ({ ...prev, [levelId]: !prev[levelId] }));
  };

  const getDialogTitle = () => {
    const titles = {
      hall: 'Add New Hall',
      row: 'Add New Row',
      rack: 'Add New Rack',
      level: storageType === 'SFG' ? 'Add New Level (Final for SFG)' : 'Add New Level',
      bin: 'Add New Bin (FG Only)'
    };
    return titles[dialogType] || 'Add New Item';
  };
  if (!sectionName) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FFFBF5] p-6" data-testid={`persectionlocationsetup-unknown`}>
        <h2 className="text-lg font-bold">Unknown section</h2>
        <p className="text-sm text-gray-500 mt-1">No catalog entry for slug <code className="font-mono">{slug}</code>.</p>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid={`persectionlocationsetup-${slug}`}>
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center">
          <Button
            variant="ghost"
            onClick={() => navigate(basePath)}
            className="text-[#F9FAFB] hover:bg-white/10 mr-4"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold font-display">WMS Location Setup</h1>
            <p className="text-sm text-[#E8DCC8]">Configure storage locations hierarchy</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Storage Type Selector & Actions */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Label className="text-[#1A1F2E] font-medium">Storage Type:</Label>
              <Select value={storageType} onValueChange={setStorageType}>
                <SelectTrigger className="w-40" data-testid="storage-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SFG">SFG Storage</SelectItem>
                  <SelectItem value="FG">FG Storage</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".csv"
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={handleExportTemplate}
                className="text-[#2D5F4F] border-[#2D5F4F]"
                data-testid="export-template-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Template
              </Button>
              <Button
                onClick={handleImportClick}
                disabled={importing}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="import-locations-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {importing ? 'Importing...' : 'Import Locations'}
              </Button>
              <Button
                onClick={() => openDialog('hall')}
                className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                data-testid="add-location-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Hall
              </Button>
            </div>
          </div>
          
          {/* Import Progress */}
          {importProgress && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                {importProgress.phase === 'starting' && 'Starting import...'}
                {importProgress.phase === 'processing' && `Processing: ${importProgress.processed}/${importProgress.total}`}
                {importProgress.phase === 'completed' && `✓ Complete! Created: ${importProgress.created}, Updated: ${importProgress.updated}`}
              </p>
              {importProgress.phase === 'processing' && (
                <div className="mt-2 h-2 bg-blue-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-600 transition-all duration-300" 
                    style={{ width: `${(importProgress.processed / importProgress.total) * 100}%` }}
                  />
                </div>
              )}
            </div>
          )}
          
          {/* Import Format Info */}
          <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-xs text-gray-600">
              <strong>CSV Format:</strong> hall_name, row_name, rack_name, level_name, capacity, velocity_class
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Example: H01, A, 01, A, 100, C → Creates bin code H01-A-01-A with capacity 100
            </p>
          </div>
        </div>

        {/* Location Hierarchy Tree */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
          <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">
            {storageType} Storage Hierarchy
          </h2>

          {loading ? (
            <div className="text-center py-8 text-[#5A5F6E]">Loading...</div>
          ) : hierarchy.length === 0 ? (
            <div className="text-center py-12">
              <Warehouse className="w-12 h-12 text-[#E5DFD0] mx-auto mb-4" />
              <p className="text-[#5A5F6E]">No halls configured yet.</p>
              <p className="text-sm text-[#5A5F6E] mt-1">Click "Add Hall" to get started.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {hierarchy.map((hall) => (
                <div key={hall.location_id} className="border border-[#E5DFD0] rounded-lg">
                  {/* Hall Level */}
                  <div 
                    className="flex items-center justify-between p-3 bg-blue-50 rounded-t-lg cursor-pointer"
                    onClick={() => toggleHall(hall.location_id)}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      {hall.rows?.length > 0 ? (
                        expandedHalls[hall.location_id] ? 
                          <ChevronDown className="w-4 h-4 text-blue-600" /> : 
                          <ChevronRight className="w-4 h-4 text-blue-600" />
                      ) : (
                        <div className="w-4" />
                      )}
                      <Warehouse className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-[#1A1F2E]">{hall.location_name}</span>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Hall</span>
                      <span className="text-xs text-[#5A5F6E] ml-2">
                        ({hall.rows?.length || 0} rows)
                      </span>
                      {/* Display assigned categories */}
                      {hall.categories && hall.categories.length > 0 && (
                        <div className="flex items-center gap-1 ml-2">
                          <Tag className="w-3 h-3 text-purple-600" />
                          <span className="text-xs text-purple-700">
                            {hall.categories.slice(0, 3).join(', ')}
                            {hall.categories.length > 3 && ` +${hall.categories.length - 3} more`}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); openCategoryDialog(hall); }}
                        className="text-purple-600 hover:text-purple-700"
                        title="Configure Categories"
                        data-testid={`configure-categories-${hall.location_id}`}
                      >
                        <Settings className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); openDialog('row', hall.location_id); }}
                        className="text-green-600 hover:text-green-700"
                        title="Add Row"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); handleDelete('hall', hall.location_id); }}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Rows */}
                  {expandedHalls[hall.location_id] && hall.rows?.map((row) => (
                    <div key={row.row_id} className="ml-6 border-l-2 border-blue-200">
                      <div 
                        className="flex items-center justify-between p-2 bg-green-50 cursor-pointer"
                        onClick={() => toggleRow(row.row_id)}
                      >
                        <div className="flex items-center gap-2">
                          {row.racks?.length > 0 ? (
                            expandedRows[row.row_id] ? 
                              <ChevronDown className="w-4 h-4 text-green-600" /> : 
                              <ChevronRight className="w-4 h-4 text-green-600" />
                          ) : (
                            <div className="w-4" />
                          )}
                          <Rows3 className="w-4 h-4 text-green-600" />
                          <span className="text-[#1A1F2E]">{row.row_name}</span>
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Row</span>
                          <span className="text-xs text-[#5A5F6E]">
                            ({row.racks?.length || 0} racks)
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); openDialog('rack', row.row_id); }}
                            className="text-purple-600 hover:text-purple-700"
                            title="Add Rack"
                          >
                            <Plus className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); handleDelete('row', row.row_id); }}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>

                      {/* Racks */}
                      {expandedRows[row.row_id] && row.racks?.map((rack) => (
                        <div key={rack.rack_id} className="ml-6 border-l-2 border-green-200">
                          <div 
                            className="flex items-center justify-between p-2 bg-purple-50 cursor-pointer"
                            onClick={() => toggleRack(rack.rack_id)}
                          >
                            <div className="flex items-center gap-2">
                              {rack.levels?.length > 0 ? (
                                expandedRacks[rack.rack_id] ? 
                                  <ChevronDown className="w-4 h-4 text-purple-600" /> : 
                                  <ChevronRight className="w-4 h-4 text-purple-600" />
                              ) : (
                                <div className="w-4" />
                              )}
                              <LayoutGrid className="w-4 h-4 text-purple-600" />
                              <span className="text-[#1A1F2E]">{rack.rack_name}</span>
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Rack</span>
                              <span className="text-xs text-[#5A5F6E]">
                                ({rack.levels?.length || 0} levels)
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => { e.stopPropagation(); openDialog('level', rack.rack_id); }}
                                className="text-orange-600 hover:text-orange-700"
                                title="Add Level"
                              >
                                <Plus className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => { e.stopPropagation(); handleDelete('rack', rack.rack_id); }}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>

                          {/* Levels */}
                          {expandedRacks[rack.rack_id] && rack.levels?.map((level) => (
                            <div key={level.level_id} className="ml-6 border-l-2 border-purple-200">
                              <div 
                                className={`flex items-center justify-between p-2 bg-orange-50 ${storageType === 'FG' ? 'cursor-pointer' : ''}`}
                                onClick={() => storageType === 'FG' && toggleLevel(level.level_id)}
                              >
                                <div className="flex items-center gap-2 flex-wrap">
                                  {storageType === 'FG' && level.bins?.length > 0 ? (
                                    expandedLevels[level.level_id] ? 
                                      <ChevronDown className="w-4 h-4 text-orange-600" /> : 
                                      <ChevronRight className="w-4 h-4 text-orange-600" />
                                  ) : (
                                    <div className="w-4" />
                                  )}
                                  <Layers className="w-4 h-4 text-orange-600" />
                                  <span className="text-[#1A1F2E]">{level.level_name}</span>
                                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">Level</span>
                                  <span className="text-xs px-2 py-0.5 bg-gray-100 rounded font-mono">
                                    {level.bin_code}
                                  </span>
                                  
                                  {/* Capacity Edit */}
                                  {editingLevelId === level.level_id ? (
                                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                      <span className="text-xs text-gray-500">Capacity:</span>
                                      <Input
                                        type="number"
                                        value={editCapacityValue}
                                        onChange={(e) => setEditCapacityValue(e.target.value)}
                                        className="w-20 h-6 text-xs"
                                        autoFocus
                                      />
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => saveCapacity(level.level_id)}
                                        className="h-6 w-6 p-0 text-green-600 hover:text-green-700"
                                      >
                                        <Check className="w-3 h-3" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={cancelEditCapacity}
                                        className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                                      >
                                        <X className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  ) : (
                                    <div 
                                      className="flex items-center gap-1 cursor-pointer hover:bg-orange-100 px-2 py-0.5 rounded group"
                                      onClick={(e) => { e.stopPropagation(); startEditCapacity(level.level_id, level.capacity); }}
                                      title="Click to edit capacity"
                                    >
                                      <span className="text-xs text-gray-500">Capacity:</span>
                                      <span className="text-xs font-medium text-[#1A1F2E]">{level.capacity || 0}</span>
                                      <Edit2 className="w-3 h-3 text-gray-400 group-hover:text-orange-600" />
                                    </div>
                                  )}
                                  
                                  {/* Velocity Class Badge */}
                                  {level.velocity_class && (
                                    <span className={`text-xs px-2 py-0.5 rounded font-bold text-white ${
                                      level.velocity_class === 'A' ? 'bg-green-500' :
                                      level.velocity_class === 'B' ? 'bg-yellow-500' :
                                      'bg-red-500'
                                    }`}>
                                      {level.velocity_class}
                                    </span>
                                  )}
                                  
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    level.status === 'available' ? 'bg-green-100 text-green-700' :
                                    level.status === 'occupied' ? 'bg-red-100 text-red-700' :
                                    'bg-yellow-100 text-yellow-700'
                                  }`}>
                                    {level.status}
                                  </span>
                                  
                                  {level.current_stock > 0 && (
                                    <span className="text-xs text-gray-500">
                                      Stock: {level.current_stock}
                                    </span>
                                  )}
                                  
                                  {storageType === 'FG' && (
                                    <span className="text-xs text-[#5A5F6E]">
                                      ({level.bins?.length || 0} bins)
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  {storageType === 'FG' && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={(e) => { e.stopPropagation(); openDialog('bin', level.level_id); }}
                                      className="text-cyan-600 hover:text-cyan-700"
                                      title="Add Bin"
                                    >
                                      <Plus className="w-3 h-3" />
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => { e.stopPropagation(); handleDelete('level', level.level_id); }}
                                    className="text-red-600 hover:text-red-700"
                                    disabled={level.current_stock > 0}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>

                              {/* Bins (FG only) */}
                              {storageType === 'FG' && expandedLevels[level.level_id] && level.bins?.map((bin) => (
                                <div key={bin.bin_id} className="ml-6 border-l-2 border-orange-200">
                                  <div className="flex items-center justify-between p-2 bg-cyan-50">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <BoxSelect className="w-4 h-4 text-cyan-600" />
                                      <span className="text-[#1A1F2E]">{bin.bin_name}</span>
                                      <span className="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded">Bin</span>
                                      <span className="text-xs px-2 py-0.5 bg-gray-100 rounded font-mono">
                                        {bin.bin_code}
                                      </span>
                                      <span className={`text-xs px-2 py-0.5 rounded ${
                                        bin.status === 'available' || bin.status === 'empty' ? 'bg-green-100 text-green-700' :
                                        bin.status === 'occupied' || bin.status === 'full' ? 'bg-red-100 text-red-700' :
                                        'bg-yellow-100 text-yellow-700'
                                      }`}>
                                        {bin.status || 'empty'}
                                      </span>
                                      <span className="text-xs text-[#5A5F6E]">
                                        ({bin.current_qty || 0}/{bin.capacity || 100})
                                      </span>
                                      {/* Display current SKU in bin (auto-assigned during put-away) */}
                                      {bin.current_sku && (
                                        <div className="flex items-center gap-1 ml-2 bg-indigo-100 px-2 py-0.5 rounded">
                                          <Tag className="w-3 h-3 text-indigo-600" />
                                          <span className="text-xs text-indigo-700 font-medium">
                                            {bin.current_sku}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleDelete('bin', bin.bin_id)}
                                        className="text-red-600 hover:text-red-700"
                                        disabled={bin.current_qty > 0}
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="mt-6 bg-white rounded-lg border border-[#E5DFD0] p-4">
          <h3 className="text-sm font-medium text-[#1A1F2E] mb-3">Hierarchy Legend</h3>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-50 border border-blue-200 rounded" />
              <span>Hall</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-50 border border-green-200 rounded" />
              <span>Row</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-purple-50 border border-purple-200 rounded" />
              <span>Rack</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-orange-50 border border-orange-200 rounded" />
              <span>Level {storageType === 'SFG' && '(Final)'}</span>
            </div>
            {storageType === 'FG' && (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-cyan-50 border border-cyan-200 rounded" />
                <span>Bin (FG only)</span>
              </div>
            )}
          </div>
          <p className="text-xs text-[#5A5F6E] mt-3">
            {storageType === 'SFG' 
              ? 'SFG: Hall → Row → Rack → Level (bundles stored at level)' 
              : 'FG: Hall → Row → Rack → Level → Bin (individual packs stored in bins)'}
          </p>
        </div>
      </main>

      {/* Add Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{getDialogTitle()}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name</Label>
              <Input
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder={`Enter ${dialogType} name`}
                className="mt-1"
                data-testid="new-item-name-input"
              />
            </div>
            {(dialogType === 'level' || dialogType === 'bin') && (
              <div>
                <Label>Capacity {dialogType === 'bin' ? '(packs)' : '(optional)'}</Label>
                <Input
                  type="number"
                  value={newItemCapacity}
                  onChange={(e) => setNewItemCapacity(e.target.value)}
                  placeholder={dialogType === 'bin' ? 'Default: 100' : 'Enter capacity'}
                  className="mt-1"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreate}
              className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
              data-testid="create-item-btn"
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Configuration Dialog for Halls */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Configure Categories for {selectedHall?.location_name}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-[#5A5F6E] mb-4">
              Select the Category 4 values that this hall should accept. Items will only be stored in this hall if they belong to one of these categories.
            </p>
            <div className="max-h-64 overflow-y-auto border rounded-lg p-3">
              {availableCategories.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No categories found in item master</p>
              ) : (
                <div className="space-y-2">
                  {availableCategories.map((category) => (
                    <label 
                      key={category}
                      className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-gray-50 ${
                        selectedCategories.includes(category) ? 'bg-purple-50 border border-purple-200' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(category)}
                        onChange={() => handleCategoryToggle(category)}
                        className="w-4 h-4 text-purple-600 rounded"
                      />
                      <span className="text-sm text-[#1A1F2E]">{category}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            {selectedCategories.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-[#5A5F6E] mb-2">Selected ({selectedCategories.length}):</p>
                <div className="flex flex-wrap gap-1">
                  {selectedCategories.map((cat) => (
                    <span key={cat} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                      {cat}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={saveHallCategories}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="save-categories-btn"
            >
              Save Categories
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PerSectionLocationSetup;
